import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { usePrintMode } from './PrintModeContext';
import * as Sentry from '@sentry/react';

/**
 * GithubIssuesHealthSummary Component
 * Displays GitHub repository issues health metrics
 * Shows open count, created/closed trends, aging buckets, and top labels
 */
const GithubIssuesHealthSummary = ({ owner = 'Capital-Land-Management-Services', repo = 'CapTrac-ReactCore' }) => {
  const { apiFetch } = useAuth();
  const isPrintMode = usePrintMode();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [issuesData, setIssuesData] = useState(null);

  const secondaryColor = getComputedStyle(document.documentElement).getPropertyValue('--secondary-color')?.trim() || '#57565B';

  // Calculate issues health score (0-100)
  // Designed for repos where GitHub Issues serve as the project management tool,
  // meaning the backlog naturally contains planned work items (features, phases)
  // alongside bugs. The score focuses on *velocity* and *bug responsiveness*
  // rather than penalizing a large backlog of planned work.
  const healthScore = useMemo(() => {
    if (!issuesData) return null;

    const created = issuesData.createdLastMonth || 0;
    const closed = issuesData.closedLastMonth || 0;
    const aging = issuesData.aging || {};
    const bugCount = aging.bugCount || 0;
    const bugAging90Plus = aging.bugAging90Plus || 0;
    const totalCounted = aging.totalCounted || 0;

    // --- 1. Throughput velocity (50% weight) ---
    // Measures whether the team is keeping up with incoming work.
    // Closure rate = closed / max(created, 1). 100 % = perfect balance, >100 % = clearing backlog.
    let velocityScore;
    if (created === 0 && closed === 0) {
      velocityScore = 70; // No activity — neutral, not penalised
    } else if (created === 0) {
      velocityScore = 100; // Only closures
    } else {
      const closureRate = closed / created;
      if (closureRate >= 1.0) velocityScore = 100;
      else if (closureRate >= 0.8) velocityScore = 85;
      else if (closureRate >= 0.5) velocityScore = 70;
      else if (closureRate >= 0.25) velocityScore = 50;
      else velocityScore = 30;
    }

    // --- 2. Bug responsiveness (30% weight) ---
    // Only bugs that sit unresolved for 90+ days are a concern.
    // Planned features/phases in the 90+ bucket are expected and not penalised.
    let bugScore = 100;
    if (bugCount > 0) {
      const staleBugRate = bugAging90Plus / bugCount;
      // Mild curve: 0 stale bugs = 100, all stale = 40
      bugScore = Math.round(100 - staleBugRate * 60);
    }

    // --- 3. Backlog freshness (20% weight) ---
    // Rewards recent activity in the backlog (items in 0-30 day buckets).
    let freshnessScore = 70; // default neutral
    if (totalCounted > 0) {
      const recentItems = (aging.days0To7 || 0) + (aging.days8To30 || 0);
      const recentRate = recentItems / totalCounted;
      // Even a small amount of recent activity is healthy
      if (recentRate >= 0.15) freshnessScore = 100;
      else if (recentRate >= 0.08) freshnessScore = 85;
      else if (recentRate >= 0.03) freshnessScore = 70;
      else freshnessScore = 55;
    }

    const score = (velocityScore * 0.50) + (bugScore * 0.30) + (freshnessScore * 0.20);
    return Math.max(0, Math.min(100, Math.round(score)));
  }, [issuesData]);

  const getScoreInterpretation = (score) => {
    if (score === null) return { text: 'Insufficient Data', color: '#999', icon: '?' };
    if (score >= 85) return { text: 'Excellent', color: '#2e7d32', icon: '✓' };
    if (score >= 70) return { text: 'Good', color: '#7cb342', icon: '✓' };
    if (score >= 55) return { text: 'Fair', color: '#ffa000', icon: '⚠' };
    if (score >= 40) return { text: 'Needs Attention', color: '#ff5722', icon: '⚠' };
    return { text: 'Critical', color: '#d32f2f', icon: '✗' };
  };

  // Fetch issues health data with cache-first strategy
  const fetchIssuesData = useCallback(async () => {
    setLoading(true);
    setError('');
    
    // Create AbortController with 60-second timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000);
    
    try {
      // Try cache first
      try {
        const cacheRes = await apiFetch(`/api/Github/issues-health/cache?owner=${owner}&repo=${repo}`, { signal: controller.signal });
        if (cacheRes.ok && cacheRes.status !== 204) {
          const cacheData = await cacheRes.json().catch(() => null);
          if (cacheData && cacheData.data) {
            setIssuesData(cacheData.data);
            setLoading(false);
            clearTimeout(timeoutId);
            return;
          }
        }
      } catch (catchError) {
          Sentry.captureException(catchError);
        // Silently fail and try live endpoint
      }

      // Fallback to live endpoint
      const response = await apiFetch(`/api/Github/issues-health?owner=${owner}&repo=${repo}`, { signal: controller.signal });
      if (!response.ok) {
        if (response.status === 404) {
          setError('Repository not found. Check owner and repository name.');
        } else if (response.status === 401) {
          setError('Unauthorized. Check GitHub token configuration.');
        } else {
          setError(`Failed to load issues data: ${response.status}`);
        }
        setIssuesData(null);
        setLoading(false);
        clearTimeout(timeoutId);
        return;
      }
      const result = await response.json().catch(() => null);
      if (result) setIssuesData(result.data);
    } catch (e) {
        Sentry.captureException(e);
      if (e.name === 'AbortError') {
        setError('Request timed out. Please try again.');
      } else {
        setError(e.message || 'Error loading issues data');
      }
      setIssuesData(null);
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  }, [apiFetch, owner, repo]);

  useEffect(() => { fetchIssuesData(); }, [fetchIssuesData]);

  // Prepare aging buckets data for chart
  const agingChartData = useMemo(() => {
    if (!issuesData?.aging) return [];
    const aging = issuesData.aging;
    return [
      { bucket: '0-7 days', count: aging.days0To7 || 0, fill: '#1a7f37' },
      { bucket: '8-30 days', count: aging.days8To30 || 0, fill: '#1f6feb' },
      { bucket: '31-90 days', count: aging.days31To90 || 0, fill: '#bf8700' },
      { bucket: '90+ days', count: aging.days90Plus || 0, fill: '#d73a4a' }
    ].filter(item => item.count > 0);
  }, [issuesData]);

  // Prepare top labels data for chart
  const labelsChartData = useMemo(() => {
    if (!issuesData?.topLabels) return [];
    return issuesData.topLabels.map(label => ({
      name: label.label,
      count: label.count,
      fill: label.color ? `#${label.color}` : secondaryColor
    }));
  }, [issuesData, secondaryColor]);

  // Format number with space separator
  const formatNumber = (n) => (Number(n) || 0).toLocaleString('en-US').replace(/,/g, ' ');

  if (loading) {
    return (
      <div className="dashboard-card">
        <h2 style={{ marginTop: 0, marginBottom: 12 }}>GitHub Issues Health</h2>
        <HexagonLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-card">
        <h2 style={{ marginTop: 0, marginBottom: 12 }}>GitHub Issues Health</h2>
        <div className="alert alert-error">{error}</div>
      </div>
    );
  }

  if (!issuesData) return null;

  const scoreInterp = getScoreInterpretation(healthScore);
  const openIssues = issuesData.openIssuesCount || 0;
  const created = issuesData.createdLastMonth || 0;
  const closed = issuesData.closedLastMonth || 0;
  const netChange = created - closed;
  const closureRate = created > 0 ? Math.round((closed / created) * 100) : (closed > 0 ? 100 : 0);
  const bugCount = issuesData.aging?.bugCount || 0;
  const bugAging90Plus = issuesData.aging?.bugAging90Plus || 0;
  const aging90Plus = issuesData.aging?.days90Plus || 0;
  const plannedItems90Plus = aging90Plus - bugAging90Plus;

  const llmProxyText = [
    `GitHub Issues Health Score: ${healthScore}/100`,
    `Repository: ${owner}/${repo}`,
    `Open Issues: ${openIssues} (${bugCount} bugs)`,
    `Created Last Month: ${created}`,
    `Closed Last Month: ${closed}`,
    `Closure Rate: ${closureRate}%`,
    `Net Change: ${netChange > 0 ? '+' : ''}${netChange}`,
    `Stale Bugs (90+ days): ${bugAging90Plus}`,
    `Planned Items 90+ days: ${plannedItems90Plus}`
  ].join('\n');

  return (
    <div className="dashboard-card group-wrapper github-issues-health-summary">
      <div data-llm-proxy style={{ display: 'none' }}>{llmProxyText}</div>
      <h2 style={{ marginTop: 0, marginBottom: 12 }}>GitHub Issues Health</h2>

      {/* Executive Summary Banner */}
      <div className="dashboard-card exec-banner" style={{ borderLeft: `4px solid ${scoreInterp.color}` }}>
        <div className="exec-banner-layout">
          <div className="exec-banner-score">
            <div className="exec-banner-value" style={{ color: scoreInterp.color }}>
              {healthScore !== null ? healthScore : '—'}
              <span className="exec-banner-suffix">/ 100</span>
            </div>
            <div className="exec-banner-label" style={{ color: scoreInterp.color }}>{scoreInterp.icon} {scoreInterp.text}</div>
            <div className="exec-banner-sublabel">Project Velocity &amp; Bug Health</div>
          </div>
          <div className="exec-banner-body">
            <div className="exec-banner-text">
              <strong>What this means:</strong> The repository tracks <strong>{openIssues} open work items</strong> (features, phases &amp; bugs).
              {closureRate >= 100 && <span style={{ color: '#2e7d32', fontWeight: 600 }}> Throughput is excellent — {closed} closed vs {created} created last month ({closureRate}% closure rate).</span>}
              {closureRate >= 80 && closureRate < 100 && <span style={{ color: '#7cb342', fontWeight: 600 }}> Throughput is healthy — {closed} closed vs {created} created last month ({closureRate}% closure rate).</span>}
              {closureRate >= 50 && closureRate < 80 && <span style={{ color: '#ffa000', fontWeight: 600 }}> Throughput is moderate — {closed} closed vs {created} created ({closureRate}% closure rate). Consider prioritising closures.</span>}
              {closureRate < 50 && created > 0 && <span style={{ color: '#d73a4a', fontWeight: 600 }}> Low throughput — only {closureRate}% closure rate ({closed} closed vs {created} created). Review team capacity.</span>}
              {bugAging90Plus > 0 && <span style={{ color: '#d73a4a', fontWeight: 600 }}> {bugAging90Plus} bug{bugAging90Plus !== 1 ? 's' : ''} unresolved for 90+ days — these need attention.</span>}
              {bugAging90Plus === 0 && bugCount > 0 && <span style={{ color: '#2e7d32', fontWeight: 600 }}> No stale bugs — all {bugCount} open bug{bugCount !== 1 ? 's' : ''} are being actively managed.</span>}
              {bugCount === 0 && <span style={{ color: '#2e7d32', fontWeight: 600 }}> No open bugs reported.</span>}
            </div>
          </div>
        </div>
      </div>

      {issuesData.warning && (
        <div className="alert alert-warning" style={{ marginBottom: 16 }}>
          {issuesData.warning}
        </div>
      )}

      {/* Key Metrics */}
      <div className="metrics-row">
        <div className="dashboard-card metric-card">
          <div className="metric-label">Total Work Items</div>
          <div className="metric-value" style={{ color: secondaryColor }}>{formatNumber(openIssues)}</div>
          <div className="metric-subtitle">{bugCount > 0 ? `${bugCount} bug${bugCount !== 1 ? 's' : ''} · ${openIssues - bugCount} planned` : 'All planned work'}</div>
        </div>
        <div className="dashboard-card metric-card">
          <div className="metric-label">Created (Month)</div>
          <div className="metric-value">{formatNumber(created)}</div>
          <div className="metric-subtitle">New work items</div>
        </div>
        <div className="dashboard-card metric-card">
          <div className="metric-label">Closed (Month)</div>
          <div className="metric-value" style={{ color: '#1a7f37' }}>{formatNumber(closed)}</div>
          <div className="metric-subtitle">Completed</div>
        </div>
        <div className="dashboard-card metric-card">
          <div className="metric-label">Closure Rate</div>
          <div className="metric-value" style={{ color: closureRate >= 80 ? '#1a7f37' : closureRate >= 50 ? '#ffa000' : '#d73a4a' }}>
            {closureRate}%
          </div>
          <div className="metric-subtitle">{netChange > 0 ? `+${netChange} net growth` : netChange < 0 ? `${netChange} net reduction` : 'Balanced'}</div>
        </div>
        {bugCount > 0 && (
          <div className="dashboard-card metric-card">
            <div className="metric-label">Stale Bugs</div>
            <div className="metric-value" style={{ color: bugAging90Plus > 0 ? '#d73a4a' : '#1a7f37' }}>
              {bugAging90Plus}
            </div>
            <div className="metric-subtitle">{bugAging90Plus > 0 ? 'Over 90 days unresolved' : 'None over 90 days'}</div>
          </div>
        )}
      </div>

      {/* Aging Buckets Chart */}
      <div className="dashboard-grid compact">
        <div className="dashboard-card chart-card">
          <h4 className="chart-title">Open Issues by Age</h4>
          <div className="chart-subtitle">Age distribution (includes features, phases &amp; bugs)</div>
          {agingChartData.length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: '#666', fontSize: 13 }}>
              No open issues
            </div>
          ) : (
            <ResponsiveContainer width={isPrintMode ? 400 : "100%"} height={280}>
              <BarChart data={agingChartData} margin={{ top: 10, right: 10, bottom: 60, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="bucket" 
                  angle={0} 
                  textAnchor="middle" 
                  interval={0}
                  height={60}
                  tick={{ fontSize: 10 }}
                />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" radius={[4, 4, 0, 0]} isAnimationActive={!isPrintMode}>
                  {agingChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Top Labels */}
        <div className="dashboard-card chart-card">
          <h4 className="chart-title">Top 5 Labels</h4>
          <div className="chart-subtitle">Most frequently used issue labels</div>
          {labelsChartData.length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: '#666', fontSize: 13 }}>
              No label data available
            </div>
          ) : (
            <ResponsiveContainer width={isPrintMode ? 400 : "100%"} height={280}>
              <BarChart data={labelsChartData} margin={{ top: 10, right: 10, bottom: 60, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name" 
                  angle={0} 
                  textAnchor="middle" 
                  interval={0}
                  height={60}
                  tick={{ fontSize: 10 }}
                />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" radius={[4, 4, 0, 0]} isAnimationActive={!isPrintMode}>
                  {labelsChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Explanation Section */}
      <div className="explanation-box">
        <strong>Understanding GitHub Issues Metrics:</strong>
        <ul>
          <li><strong>Project Velocity &amp; Bug Health Score:</strong> Combines throughput velocity (50%), bug responsiveness (30%), and backlog freshness (20%). Designed for repositories that use GitHub Issues as a project management tool, where a large backlog of planned features and phases is expected and healthy.</li>
          <li><strong>Closure Rate:</strong> Percentage of issues closed vs created each month. ≥80% indicates the team is keeping pace with incoming work; &lt;50% signals a capacity concern.</li>
          <li><strong>Stale Bugs:</strong> Bug-labeled issues open for 90+ days. Unlike planned features, bugs sitting unresolved this long are a genuine concern and significantly impact the score.</li>
          <li><strong>Planned Work (90+ days):</strong> Features, phases, and enhancements in the 90+ day bucket are normal for project management and are <em>not</em> penalised in the score. These represent future-phase scope.</li>
          <li><strong>Backlog Freshness:</strong> Measures recent activity — repos with items created or updated in the last 30 days show active planning and engagement.</li>
          <li><strong>Top Labels:</strong> Most common categorisation tags — helps identify recurring themes and project phase distribution.</li>
        </ul>
      </div>
    </div>
  );
};

export default GithubIssuesHealthSummary;
